import { isGphFromSources } from '../automatic-distribution-employee';
import { Component, ElementRef, Inject, Input, TemplateRef, ViewChild, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TICKETS_PATHS } from '../../../../../../tickets/tickets';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-automatic-distribution-result-new',
  templateUrl: './automatic-distribution-result.component.html',
  styleUrls: ['./automatic-distribution-result.component.scss'],
  standalone: false,
})
export class AutomaticDistributionResultComponent {
  @Input() automaticDistribution;
  @Input() tickets;
  @Input() brigades;

  public routeGphResolver = (route) => this.isRouteEmployeeGph(route);

  getRouteExportName(route): string {
    return ((route && route.employee_name) || '') + (this.isRouteEmployeeGph(route) ? ' [ГПХ]' : '');
  }

  @ViewChild('exportContainer') exportContainer: ElementRef;
  @ViewChild('exportFormatModal') exportFormatModal: TemplateRef<any>;

  private jsZipLoadingPromise: Promise<any>;
  private xlsxLoadingPromise: Promise<any>;
  public exportFormat: 'html' | 'excel' = 'html';
  public exportResultError = '';
  public moment = require('moment');
  public TICKETS_PATHS = TICKETS_PATHS;

  constructor(@Inject(DOCUMENT) private document: Document, private modalService: NgbModal) {}

  get hasVisiblePanel(): boolean {
    return this.automaticDistribution && (this.automaticDistribution.showMap || this.automaticDistribution.showSchedule);
  }

  get result() {
    return this.getResultData(this.automaticDistribution && this.automaticDistribution.result);
  }

  get hasResultAppliedStatus(): boolean {
    const isApplied = this.result && this.result.is_applied;

    return isApplied === true || isApplied === false;
  }

  get isResultApplied(): boolean {
    return !!this.result && this.result.is_applied === true;
  }

  get summary() {
    return this.result && this.result.summary;
  }

  get routes(): any[] {
    return (this.result && this.result.routes) || [];
  }

  get distributionEmployees(): any[] {
    const settings = this.automaticDistribution && this.automaticDistribution.lastSettings;

    return this.getInputEmployees().concat((settings && settings.employees) || []);
  }

  get unassignedOrders(): any[] {
    return (this.result && this.result.unassigned_orders) || [];
  }

  get unassignedAnalysis(): any[] {
    const diagnostics = this.result && this.result.diagnostics;

    return (diagnostics && diagnostics.ai_unassigned_analysis) || [];
  }

  get routeOrdersCount(): number {
    return this.routes.reduce((count, route) => count + this.getRouteOrders(route).length, 0);
  }

  get totalOrdersCount(): number {
    return this.getSummaryNumber('total_orders', this.routeOrdersCount + this.unassignedOrders.length);
  }

  get plannedOrdersCount(): number {
    return this.getSummaryNumber('planned_orders', this.routeOrdersCount);
  }

  get unassignedOrdersCount(): number {
    return this.getSummaryNumber('unassigned_orders', this.unassignedOrders.length);
  }

  get travelTimeMinutes(): number {
    return this.getSummaryNumber('total_travel_time_minutes', 0);
  }

  get travelDistanceMeters(): number {
    return this.getSummaryNumber('total_travel_distance_meters', 0);
  }

  get fallbackMatrixText(): string {
    if (!this.summary || this.summary.used_fallback_matrix === null || this.summary.used_fallback_matrix === undefined) {
      return '-';
    }

    return this.summary.used_fallback_matrix ? 'Да' : 'Нет';
  }

  get hasDistributionData(): boolean {
    return !!this.summary || this.routes.length > 0 || this.unassignedOrders.length > 0;
  }

  get hasSuccessfulDistribution(): boolean {
    return this.hasDistributionData && this.unassignedOrdersCount === 0 && this.totalOrdersCount > 0;
  }

  get screenTitle(): string {
    return this.automaticDistribution && this.automaticDistribution.showSchedule ? 'Скорректированное расписание' : 'Карта скорректированного маршрута';
  }

  get screenSubtitle(): string {
    return this.automaticDistribution && this.automaticDistribution.showSchedule
      ? 'Диаграмма маршрутов построена по последнему результату распределения.'
      : 'Карта маршрутов построена по последнему результату распределения.';
  }

  get emptyResultText(): string {
    return this.automaticDistribution && this.automaticDistribution.showSchedule
      ? 'Результат распределения загружен, но в ответе нет данных для отображения расписания.'
      : 'Результат распределения загружен, но в ответе нет данных для отображения карты маршрутов.';
  }

  get showSaveDistributionButton(): boolean {
    const settings = this.automaticDistribution && this.automaticDistribution.lastSettings;
    const isPreviewMode = this.isPreviewMode(settings);

    return !!(
      this.automaticDistribution &&
      this.automaticDistribution.showMap &&
      (!settings || settings.scenario !== 'analysis') &&
      isPreviewMode &&
      !this.automaticDistribution.saveResultSuccess
    );
  }

  get isSaveDistributionDisabled(): boolean {
    return !!(
      !this.automaticDistribution ||
      this.automaticDistribution.isSavingResult ||
      !this.automaticDistribution.transactionId
    );
  }

  get isSaveResultDisabled(): boolean {
    return !!(
      !this.result ||
      !this.automaticDistribution ||
      this.automaticDistribution.resultLoading ||
      this.automaticDistribution.originalTicketsLoading ||
      this.automaticDistribution.resultError
    );
  }

  get titleDate(): string {
    const date = this.findResultDate();

    return date ? this.moment(date).format('YYYY-MM-DD') : '';
  }

  getRouteOrders(route): any[] {
    return (route && route.route) || [];
  }

  getRouteTransportIcon(route): 'car' | 'run' | 'bus' {
    const transportType = this.getRouteTransportType(route);

    if (transportType === 'driving') {
      return 'car';
    }

    if (transportType === 'public_transport') {
      return 'bus';
    }

    return 'run';
  }

  getRouteTransportTitle(route): string {
    const transportType = this.getRouteTransportType(route);

    if (transportType === 'driving') {
      return 'На машине';
    }

    if (transportType === 'public_transport') {
      return 'На общественном транспорте';
    }

    return 'Пешком';
  }

  getOrderAddress(order): string {
    const ticket = this.getTicket(order);
    const inputAddress = this.getInputAddressByOrder(order);
    const coordinateText = this.getCoordinateText(order) || this.getCoordinateText(inputAddress);

    return (ticket && ticket.hd_address) || (order && order.address) || this.getAddressTitle(inputAddress) || coordinateText || '-';
  }

  getOrderDistrict(order): string {
    const sources = [
      this.getTicket(order) || this.getOriginalTicket(order),
      order,
      this.getInputOrder(order),
      this.getUnassignedOrder(order),
      this.getInputAddressByOrder(order),
    ];
    const districtName = sources.map((source) => this.getDistrictName(source)).find((name) => !!name);

    return districtName || this.getDistrictNameById(sources) || '';
  }

  getOrderSubtype(order): string {
    const ticket = this.getTicket(order) || this.getOriginalTicket(order);

    // У подключений в подтипе лежит тариф («Тариф UP. Кот / 100 Мбит/с»),
    // аналитике нужен тип заявки из HD — например «Конвергенция абонента»
    if (this.isConnectionTicket(ticket)) {
      return (ticket && ticket.hd_ticket_type && ticket.hd_ticket_type.name) || '-';
    }

    return (ticket && ticket.ticket_subtype) || (order && order.ticket_subtype) || '-';
  }

  /**
   * Грузовик у заявок с переездом — по одному флагу, как в блоке признаков
   * заявки (app-tickets-extra-new): autotrack = «Переезд». Тип заявки
   * не проверяем: переезд с дозаказом приходит типом «Дозаказ»,
   * и грузовик у него тоже должен быть
   */
  isOrderMoveAddr(order): boolean {
    const ticket = this.getTicket(order) || this.getOriginalTicket(order);

    return !!(ticket && ticket.extra_flags && ticket.extra_flags.moveaddr);
  }

  private isConnectionTicket(ticket): boolean {
    return this.toNumber(ticket && ticket.ticket_type && ticket.ticket_type.id) === 1;
  }

  getOrderNumber(order): string {
    const ticket = this.getTicket(order);
    const value = (ticket && ticket.hd_ticket_number) || (order && (order.hd_ticket_number || order.order_id));

    return value ? String(value) : '-';
  }

  getOrderLink(order): string[] {
    const ticketId = order && (order.order_id || order.id);

    return ticketId ? ['/' + this.TICKETS_PATHS.ticketLink + '/' + ticketId] : null;
  }

  getAnalysisTitle(analysis): string {
    if (analysis && this.toNumber(analysis.order_id)) {
      return `Анализ заявки № ${this.getOrderNumber(analysis)}`;
    }

    return 'Общий анализ нераспределенных заявок';
  }

  getAnalysisOrderInfo(analysis): { type: string; subtype: string; hdSlot: string; district: string; address: string } {
    if (!analysis || !this.toNumber(analysis.order_id)) {
      return null;
    }

    const type = this.getAnalysisOrderType(analysis);
    const subtypeValue = this.getOrderSubtype(analysis);
    const hdSlotValue = this.getOrderHdSlot(analysis);
    const subtype = subtypeValue === '-' ? '' : subtypeValue;
    const hdSlot = hdSlotValue === '-' ? '' : hdSlotValue;
    const district = this.getOrderDistrict(analysis);
    const address = this.getAnalysisOrderAddress(analysis);

    return type || subtype || hdSlot || district || address
      ? { type: type, subtype: subtype, hdSlot: hdSlot, district: district, address: address }
      : null;
  }

  isRouteEmployeeOnTrip(route): boolean {
    // Снимок на момент расчёта — источник истины, в том числе при is_trip: false.
    const savedEmployee = this.getInputEmployees().find(
      (employee) => Number(employee && employee.id) === Number(route && route.employee_id)
    );
    if (savedEmployee && typeof savedEmployee.is_trip === 'boolean') {
      return savedEmployee.is_trip;
    }

    const source = this.getRouteEmployeeSources(route).find(
      (employee) => employee && employee.is_trip !== undefined && employee.is_trip !== null
    );

    return this.isTruthyMarker(source && source.is_trip);
  }

  isRouteEmployeeGph(route): boolean {
    return isGphFromSources(this.getRouteEmployeeSources(route));
  }

  formatAnalysisText(value, analysis): string {
    const text = String(value || '');
    const employeeId = this.getAnalysisEmployeeId(analysis);
    const employeeName = employeeId && this.getAnalysisEmployeeName(analysis, employeeId);

    if (!employeeId || !employeeName) {
      return text;
    }

    return text
      .replace(
        new RegExp(`fixed_employee_id\\s*\\(\\s*${employeeId}\\s*\\)`, 'gi'),
        `фиксированный сотрудник (${employeeName})`
      )
      .replace(new RegExp(`\\bID\\s*${employeeId}\\b`, 'gi'), employeeName)
      .replace(
        new RegExp(`(сотрудник(?:а|у|ом|е|и|ов|ам|ами|ах)?\\s+)${employeeId}\\b`, 'gi'),
        `$1${employeeName}`
      )
      .replace(/fixed_employee_id/gi, `фиксированного сотрудника «${employeeName}»`);
  }

  getAnalysisRecommendations(analysis): string[] {
    const recommendations = analysis && analysis.recommendations;

    if (Array.isArray(recommendations)) {
      return recommendations
        .filter((recommendation) => !!recommendation)
        .map((recommendation) => this.formatAnalysisText(recommendation, analysis));
    }

    return recommendations ? [this.formatAnalysisText(recommendations, analysis)] : [];
  }

  getAnalysisSource(analysis): string {
    return analysis ? 'ai' : '';
  }

  formatConfidence(value): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const confidence = Number(value);

    if (!Number.isFinite(confidence)) {
      return String(value);
    }

    return confidence >= 0 && confidence <= 1 ? `${Math.round(confidence * 100)}%` : String(confidence);
  }

  formatThresholdRatio(value): string {
    const ratio = Number(value);

    return Number.isFinite(ratio) ? `${Math.round(ratio * 100)}%` : String(value);
  }

  formatDateTime(value): string {
    if (!value) {
      return '-';
    }

    return this.moment(value).format('YYYY-MM-DD HH:mm');
  }

  getOrderHdSlot(order): string {
    const ticket = this.getTicket(order) || this.getOriginalTicket(order);
    const start =
      (ticket && ticket.hd_schedule_intbegin) ||
      (order && (order.service_window_start || order.facility_start_time));
    const end =
      (ticket && ticket.hd_schedule_intend) ||
      (order && (order.service_window_end || order.facility_end_time));

    if (!start && !end) {
      return '-';
    }

    return `${start ? this.moment(start).format('HH:mm') : '-'} – ${end ? this.moment(end).format('HH:mm') : '-'}`;
  }

  formatDistance(value): string {
    const distance = Number(value || 0);

    if (distance >= 1000) {
      return `${Math.round(distance / 100) / 10} км`;
    }

    return `${Math.round(distance)} м`;
  }

  trackByRoute(index: number, route): string {
    return String((route && route.employee_id) || index);
  }

  trackByOrder(index: number, order): string {
    return String((order && order.order_id) || index);
  }

  trackByAnalysis(index: number, analysis): string {
    return String((analysis && analysis.order_id) || index);
  }

  saveDistribution(): void {
    if (this.automaticDistribution && this.automaticDistribution.saveResult) {
      this.automaticDistribution.saveResult();
    }
  }

  openExportFormatModal(): void {
    if (this.isSaveResultDisabled || !this.exportFormatModal) {
      return;
    }

    this.exportFormat = 'html';
    this.exportResultError = '';
    const context = this.automaticDistribution && this.automaticDistribution.exportContext;

    if (!context || !context.sectorName) {
      this.exportResultError = 'Не удалось определить выбранный участок. Обновите страницу и повторите попытку.';
      return;
    }

    const modalRef = this.modalService.open(this.exportFormatModal, {
      ariaLabelledBy: 'automatic-result-export-title',
      windowClass: 'modal-wrapper',
      backdrop: 'static',
    });

    modalRef.result.then(
      (format) => {
        if (format === 'excel') {
          this.saveResultExcel();
          return;
        }

        this.saveResultHtml();
      },
      () => {}
    );
  }

  saveResultHtml(): void {
    if (this.isSaveResultDisabled || !this.exportContainer) {
      return;
    }

    const source = this.exportContainer.nativeElement as HTMLElement;
    const snapshot = source.cloneNode(true) as HTMLElement;
    const snapshotWidth = Math.ceil(source.getBoundingClientRect().width || source.scrollWidth);

    this.synchronizeExportControls(source, snapshot);
    this.removeExportControls(snapshot);

    snapshot.style.margin = '0';
    snapshot.style.maxWidth = 'none';
    snapshot.style.width = `${snapshotWidth}px`;

    const title = this.automaticDistribution.showSchedule
      ? 'Скорректированное расписание'
      : 'Карта скорректированного маршрута';
    const documentHtml = this.makeExportDocument(snapshot.outerHTML, title);
    const blob = new Blob([documentHtml], { type: 'text/html;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const downloadLink = this.document.createElement('a');

    downloadLink.href = objectUrl;
    downloadLink.download = this.getExportFileName();
    downloadLink.style.display = 'none';
    this.document.body.appendChild(downloadLink);
    downloadLink.click();
    this.document.body.removeChild(downloadLink);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }

  saveResultExcel(): void {
    if (this.isSaveResultDisabled) {
      return;
    }

    Promise.all([this.loadXlsx(), this.loadJsZip()])
      .then(([xlsx, jsZip]) => this.downloadExcelWorkbook(xlsx, jsZip))
      .catch(() => {
        this.exportResultError = 'Не удалось подготовить Excel-файл. Повторите попытку.';
      });
  }

  private isPreviewMode(settings): boolean {
    if (!settings) {
      return false;
    }

    if (settings.launch_mode) {
      return settings.launch_mode === 'preview';
    }

    return settings.assign_after_result === false;
  }

  private makeExportDocument(content: string, title: string): string {
    const baseHref = this.escapeHtml(this.document.baseURI || window.location.href);
    const styles = this.getExportStyles();

    return `<!doctype html>
<html lang="ru">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <base href="${baseHref}">
  <title>${this.escapeHtml(title)}</title>
  ${styles}
  <style>
    html, body { background: #fff; margin: 0; min-height: 100%; padding: 0; }
    body { box-sizing: border-box; overflow: auto; padding: 20px; width: max-content; }
    *, *::before, *::after { box-sizing: border-box; }
    input, select, textarea { pointer-events: none; }
  </style>
</head>
<body>
${content}
</body>
</html>`;
  }

  private getExportStyles(): string {
    const styleElements = this.document.querySelectorAll('style, link[rel="stylesheet"]');

    return Array.prototype.map
      .call(styleElements, (styleElement: HTMLElement) => {
        const styleClone = styleElement.cloneNode(true) as HTMLElement;

        if (styleElement.tagName.toLowerCase() === 'link') {
          styleClone.setAttribute('href', (styleElement as HTMLLinkElement).href);
        }

        return styleClone.outerHTML;
      })
      .join('\n');
  }

  private synchronizeExportControls(source: HTMLElement, snapshot: HTMLElement): void {
    const sourceControls = source.querySelectorAll('input, textarea, select');
    const snapshotControls = snapshot.querySelectorAll('input, textarea, select');

    Array.prototype.forEach.call(sourceControls, (sourceControl: HTMLInputElement, index: number) => {
      const snapshotControl = snapshotControls[index] as HTMLInputElement;

      if (!snapshotControl) {
        return;
      }

      if (sourceControl.type === 'checkbox' || sourceControl.type === 'radio') {
        if (sourceControl.checked) {
          snapshotControl.setAttribute('checked', 'checked');
        } else {
          snapshotControl.removeAttribute('checked');
        }
        return;
      }

      snapshotControl.setAttribute('value', sourceControl.value);
    });
  }

  private removeExportControls(snapshot: HTMLElement): void {
    const controls = snapshot.querySelectorAll('button, .leaflet-control-container');

    Array.prototype.forEach.call(controls, (control: HTMLElement) => {
      if (control.parentNode) {
        control.parentNode.removeChild(control);
      }
    });

    const actions = snapshot.querySelector('.automatic-result__actions');

    if (actions && actions.parentNode) {
      actions.parentNode.removeChild(actions);
    }
  }

  private getExportFileName(): string {
    return `${this.getExportBaseFileName()}.html`;
  }

  private loadXlsx(): Promise<any> {
    const appWindow = this.document.defaultView as any;

    if (appWindow && appWindow.XLSX) {
      return Promise.resolve(appWindow.XLSX);
    }
    if (this.xlsxLoadingPromise) {
      return this.xlsxLoadingPromise;
    }

    this.xlsxLoadingPromise = new Promise((resolve, reject) => {
      const script = this.document.createElement('script');

      script.src = new URL('assets/vendor/xlsx/xlsx.full.min.js', this.document.baseURI).toString();
      script.async = true;
      script.onload = () => {
        if (appWindow && appWindow.XLSX) {
          resolve(appWindow.XLSX);
          return;
        }

        this.xlsxLoadingPromise = null;
        reject(new Error('XLSX library is unavailable'));
      };
      script.onerror = () => {
        this.xlsxLoadingPromise = null;
        reject(new Error('Unable to load XLSX library'));
      };
      this.document.body.appendChild(script);
    });

    return this.xlsxLoadingPromise;
  }

  private loadJsZip(): Promise<any> {
    const appWindow = this.document.defaultView as any;

    if (appWindow && appWindow.JSZip) {
      return Promise.resolve(appWindow.JSZip);
    }
    if (this.jsZipLoadingPromise) {
      return this.jsZipLoadingPromise;
    }

    this.jsZipLoadingPromise = new Promise((resolve, reject) => {
      const script = this.document.createElement('script');

      script.src = new URL('assets/vendor/jszip/jszip.min.js', this.document.baseURI).toString();
      script.async = true;
      script.onload = () => {
        if (appWindow && appWindow.JSZip) {
          resolve(appWindow.JSZip);
          return;
        }

        this.jsZipLoadingPromise = null;
        reject(new Error('JSZip library is unavailable'));
      };
      script.onerror = () => {
        this.jsZipLoadingPromise = null;
        reject(new Error('Unable to load JSZip library'));
      };
      this.document.body.appendChild(script);
    });

    return this.jsZipLoadingPromise;
  }

  private downloadExcelWorkbook(xlsx, jsZip): Promise<void> {
    const workbook = xlsx.utils.book_new();

    this.getExcelWorksheets().forEach((worksheetData) => {
      const worksheet = xlsx.utils.aoa_to_sheet(worksheetData.rows);
      const columnsCount = worksheetData.rows.reduce(
        (count, row) => Math.max(count, (row && row.length) || 0),
        0
      );

      worksheet['!cols'] = Array.from({ length: columnsCount }).map((unused, columnIndex) => ({
        wch: this.getExcelColumnWidth(worksheetData.rows, columnIndex),
      }));

      if (worksheetData.rows.length && columnsCount) {
        worksheet['!autofilter'] = {
          ref: xlsx.utils.encode_range({
            s: { r: 0, c: 0 },
            e: { r: worksheetData.rows.length - 1, c: columnsCount - 1 },
          }),
        };
      }

      xlsx.utils.book_append_sheet(workbook, worksheet, worksheetData.name);
    });

    const workbookData = xlsx.write(workbook, {
      bookType: 'xlsx',
      compression: true,
      type: 'array',
    });

    return jsZip
      .loadAsync(workbookData)
      .then((archive) => {
        const stylesFile = archive.file('xl/styles.xml');

        if (!stylesFile) {
          throw new Error('XLSX styles are unavailable');
        }

        return stylesFile.async('string').then((stylesXml) => {
          const headerStyle = this.addExcelHeaderStyle(stylesXml);

          archive.file('xl/styles.xml', headerStyle.xml);

          const sheetTasks = workbook.SheetNames.map((sheetName, index) => {
            const path = `xl/worksheets/sheet${index + 1}.xml`;
            const sheetFile = archive.file(path);

            if (!sheetFile) {
              return Promise.resolve();
            }

            return sheetFile.async('string').then((sheetXml) => {
              archive.file(path, this.prepareExcelSheet(sheetXml, headerStyle.styleIndex));
            });
          });

          return Promise.all(sheetTasks).then(() =>
            archive.generateAsync({
              compression: 'DEFLATE',
              compressionOptions: { level: 6 },
              type: 'arraybuffer',
            })
          );
        });
      })
      .then((workbookWithFrozenHeaders) => {
        const blob = new Blob([workbookWithFrozenHeaders], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        this.downloadBlob(blob, this.getExcelExportFileName());
      });
  }

  private prepareExcelSheet(sheetXml: string, headerStyleIndex: number): string {
    const pane = '<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>';
    const selfClosingSheetView = /<sheetView([^>]*)\/>/;
    let preparedXml = sheetXml;

    if (selfClosingSheetView.test(sheetXml)) {
      preparedXml = sheetXml.replace(selfClosingSheetView, `<sheetView$1>${pane}</sheetView>`);
    } else {
      preparedXml = sheetXml.replace(/<sheetView([^>]*)>/, `<sheetView$1>${pane}`);
    }

    return preparedXml.replace(/<row r="1"([^>]*)>([\s\S]*?)<\/row>/, (row, attributes, cells) => {
      const styledCells = cells.replace(/<c ([^>]*)>/g, `<c s="${headerStyleIndex}" $1>`);

      return `<row r="1"${attributes} ht="30" customHeight="1">${styledCells}</row>`;
    });
  }

  private addExcelHeaderStyle(stylesXml: string): { xml: string; styleIndex: number } {
    const fontsCountMatch = stylesXml.match(/<fonts count="(\d+)">/);
    const fillsCountMatch = stylesXml.match(/<fills count="(\d+)">/);
    const cellFormatsCountMatch = stylesXml.match(/<cellXfs count="(\d+)">/);

    if (!fontsCountMatch || !fillsCountMatch || !cellFormatsCountMatch) {
      throw new Error('Unexpected XLSX styles structure');
    }

    const fontId = Number(fontsCountMatch[1]);
    const fillId = Number(fillsCountMatch[1]);
    const styleIndex = Number(cellFormatsCountMatch[1]);
    const headerFont =
      '<font><b/><sz val="11"/><color rgb="FF172B4D"/><name val="Calibri"/><family val="2"/></font>';
    const headerFill =
      '<fill><patternFill patternType="solid"><fgColor rgb="FFD9EAF7"/><bgColor indexed="64"/></patternFill></fill>';
    const headerFormat =
      `<xf numFmtId="0" fontId="${fontId}" fillId="${fillId}" borderId="0" xfId="0" ` +
      'applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>';
    let preparedXml = stylesXml
      .replace(`<fonts count="${fontId}">`, `<fonts count="${fontId + 1}">`)
      .replace('</fonts>', `${headerFont}</fonts>`)
      .replace(`<fills count="${fillId}">`, `<fills count="${fillId + 1}">`)
      .replace('</fills>', `${headerFill}</fills>`)
      .replace(`<cellXfs count="${styleIndex}">`, `<cellXfs count="${styleIndex + 1}">`)
      .replace('</cellXfs>', `${headerFormat}</cellXfs>`);

    return {
      xml: preparedXml,
      styleIndex: styleIndex,
    };
  }

  private getExcelExportFileName(): string {
    return `${this.getExportBaseFileName()}.xlsx`;
  }

  private getExportBaseFileName(): string {
    const context = (this.automaticDistribution && this.automaticDistribution.exportContext) || {};
    const date = this.titleDate ? this.moment(this.titleDate).format('DD.MM.YYYY') : this.moment().format('DD.MM.YYYY');
    const time = this.moment().format('HH-mm-ss');
    const fileNameParts = ['Автоматическое распределение', context.sectorName, date, time].filter(Boolean);
    const fileName = fileNameParts.join('_');

    return fileName.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim();
  }

  private getExcelWorksheets(): any[] {
    return [
      { name: 'Все заявки', rows: this.getExcelAllOrdersRows() },
      { name: 'Сводка', rows: this.getExcelSummaryRows() },
      { name: 'Сотрудники', rows: this.getExcelEmployeesRows() },
      { name: 'Расписание', rows: this.getExcelScheduleRows() },
      { name: 'Маршруты карты', rows: this.getExcelMapRoutesRows() },
      { name: 'Геометрия', rows: this.getExcelGeometryRows() },
      { name: 'Нераспределенные', rows: this.getExcelUnassignedRows() },
      { name: 'AI-анализ', rows: this.getExcelAnalysisRows() },
      { name: 'Исходные заявки', rows: this.getExcelInputOrdersRows() },
      { name: 'Исходные сотрудники', rows: this.getExcelInputEmployeesRows() },
      { name: 'Исходные адреса', rows: this.getExcelInputAddressesRows() },
    ];
  }

  private getExcelColumnWidth(rows: any[][], columnIndex: number): number {
    const maxLength = (rows || []).reduce((length, row) => {
      const value = row && row[columnIndex];
      const normalizedValue = this.normalizeExcelValue(value);

      return Math.max(length, String(normalizedValue).length);
    }, 0);

    return Math.max(10, Math.min(45, maxLength + 2));
  }

  private normalizeExcelValue(value): string | number {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'boolean') {
      return value ? 'Да' : 'Нет';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return value;
  }

  private getExcelCurrentOrderLabel(order): string {
    const inputOrder = this.getInputOrder(order);
    const ticket = this.getTicket(order);
    const orderId = this.toNumber(order && (order.order_id || order.id));
    const number = this.getOrderNumber({ order_id: orderId });
    const name =
      (order && (order.order_name || order.name)) ||
      (inputOrder && (inputOrder.order_name || inputOrder.name)) ||
      (ticket && ticket.ticket_type && ticket.ticket_type.name) ||
      'Заявка';

    return [number && number !== '-' ? `№ ${number}` : '', name].filter(Boolean).join(' — ');
  }

  private formatExcelInterval(start, end): string {
    if (!start && !end) {
      return '';
    }

    return `${start ? this.formatDateTime(start) : '-'} — ${end ? this.formatDateTime(end) : '-'}`;
  }

  private getExcelActualPlan(order): any {
    const ticket = this.getOriginalTicket(order);
    const inputOrder = this.getInputOrder(order);
    const ticketBrigade = ticket && ticket.brigade;
    const brigadeId = this.toNumber(ticketBrigade && (ticketBrigade.id || ticketBrigade.brigade_id));
    const brigade = (this.brigades || []).find(
      (item) => this.toNumber(item && (item.id || item.brigade_id)) === brigadeId
    );
    const inputEmployeeId = this.toNumber(
      inputOrder && (inputOrder.employee_id || inputOrder.fixed_employee_id)
    );
    const brigadeEmployeeId = this.getBrigadeEmployeeId(brigade || ticketBrigade);
    const employeeId = inputEmployeeId !== null ? inputEmployeeId : brigadeEmployeeId;
    const brigadeName =
      (brigade && (brigade.brigade_name || brigade.employee_name)) ||
      (ticketBrigade && (ticketBrigade.brigade_name || ticketBrigade.employee_name));
    const employeeName = this.getExcelActualPlanEmployeeName(
      employeeId,
      brigade || ticketBrigade,
      brigadeName
    );

    if (employeeId === null && !employeeName) {
      return null;
    }

    return {
      employeeId: employeeId,
      employeeName: employeeName,
      start:
        (ticket && ticket.hd_schedule_intbegin) ||
        (inputOrder && inputOrder.date_and_time_of_service_start),
      end:
        (ticket && ticket.hd_schedule_intend) ||
        (inputOrder && inputOrder.date_and_time_of_service_end),
    };
  }

  private getExcelActualPlanEmployeeName(employeeId: number | null, brigade, brigadeName): string {
    const route = employeeId !== null
      ? this.routes.find((item) => this.toNumber(item && item.employee_id) === employeeId)
      : null;
    const brigadeHead = brigade && (brigade.brigade_head || brigade.head || brigade.employee);

    return (
      (employeeId !== null && this.getExcelEmployeeName(employeeId)) ||
      (route && route.employee_name) ||
      this.getEmployeeDisplayName(brigadeHead) ||
      brigadeName ||
      (employeeId !== null ? String(employeeId) : '')
    );
  }

  private getOriginalTicket(order) {
    const orderId = this.toNumber(order && (order.order_id || order.id));

    if (orderId === null) {
      return null;
    }

    return this.getOriginalTicketsFlatList().find(
      (ticket) => this.toNumber(ticket && ticket.id) === orderId
    ) || null;
  }

  private getOriginalTicketsFlatList(): any[] {
    const originalTickets = this.automaticDistribution && this.automaticDistribution.originalTickets;
    const dates = Array.isArray(originalTickets)
      ? originalTickets
      : originalTickets && Array.isArray(originalTickets.data)
      ? originalTickets.data
      : [];
    const tickets = [];

    dates.forEach((date) =>
      ((date && date.intervals) || []).forEach((interval) =>
        ((interval && interval.districts) || []).forEach((district) =>
          ((district && district.tickets) || []).forEach((ticket) => tickets.push(ticket))
        )
      )
    );

    return tickets;
  }

  private getBrigadeEmployeeId(brigade): number | null {
    if (!brigade) {
      return null;
    }

    const brigadeHead = brigade.brigade_head || brigade.head || brigade.employee;

    return this.toNumber(
      brigade.user_id ||
        brigade.employee_id ||
        brigade.e_id ||
        (brigadeHead && (brigadeHead.u_id || brigadeHead.user_id || brigadeHead.employee_id || brigadeHead.id))
    );
  }

  private getExcelSummaryRows(): any[][] {
    const diagnostics = (this.result && this.result.diagnostics) || {};

    return [
      [
        'Дата',
        'Всего заявок',
        'Распределено',
        'Не распределено',
        'Время в пути, мин',
        'Дистанция, м',
        'Резервная матрица',
        'Статус решателя',
        'Время решателя, сек',
        'Профиль маршрутизации',
        'Источник матрицы',
      ],
      [
        this.titleDate,
        this.totalOrdersCount,
        this.plannedOrdersCount,
        this.unassignedOrdersCount,
        this.travelTimeMinutes,
        this.travelDistanceMeters,
        this.fallbackMatrixText,
        diagnostics.solver_status,
        diagnostics.solver_time_seconds,
        diagnostics.osrm_profile,
        diagnostics.matrix_source,
      ],
    ];
  }

  private getExcelAllOrdersRows(): any[][] {
    const recordsById = {};
    const orderIds = [];

    const addOrder = (source, section: string, route = null) => {
      const orderId = this.toNumber(source && (source.order_id || source.id));

      if (orderId === null) {
        return;
      }

      if (!recordsById[orderId]) {
        recordsById[orderId] = {};
        orderIds.push(orderId);
      }

      recordsById[orderId][section] = source;

      if (route) {
        recordsById[orderId].route = route;
      }
    };

    this.getInputOrders().forEach((order) => addOrder(order, 'input'));
    this.routes.forEach((route) => this.getRouteOrders(route).forEach((order) => addOrder(order, 'result', route)));
    this.unassignedOrders.forEach((order) => addOrder(order, 'unassigned'));
    this.unassignedAnalysis.forEach((analysis) => addOrder(analysis, 'analysis'));

    const records = orderIds.map((orderId) => {
      const sources = recordsById[orderId];
      const inputOrder = sources.input;
      const resultOrder = sources.result;
      const unassignedOrder = sources.unassigned;
      const analysis = sources.analysis;
      const route = sources.route;
      const order = resultOrder || unassignedOrder || inputOrder || analysis;
      const actualPlan = this.getExcelActualPlan(order);
      const flexibleSlotsStart = resultOrder && (resultOrder.work_start_time || resultOrder.arrival_time);
      const flexibleSlotsEnd = resultOrder && resultOrder.work_end_time;
      const currentSlotsStart =
        (resultOrder && (resultOrder.service_window_start || resultOrder.facility_start_time)) ||
        (inputOrder && inputOrder.date_and_time_of_service_start);
      const currentSlotsEnd =
        (resultOrder && (resultOrder.service_window_end || resultOrder.facility_end_time)) ||
        (inputOrder && inputOrder.date_and_time_of_service_end);
      const record = {
        'ID заявки': orderId,
        'Номер заявки': this.getOrderNumber({ order_id: orderId }),
        'Статус распределения': resultOrder ? 'Распределена' : 'Не распределена',
        'Наименование': (order && (order.order_name || order.name)) || (inputOrder && inputOrder.name),
        'Район': this.getOrderDistrict(order),
        'Адрес': this.getOrderAddress(order),
        'Текущая заявка': this.getExcelCurrentOrderLabel(order),
        'Гибкие слоты': this.formatExcelInterval(flexibleSlotsStart, flexibleSlotsEnd),
        'Текущие слоты': this.formatExcelInterval(currentSlotsStart, currentSlotsEnd),
        'Актуальный план': this.formatExcelInterval(actualPlan && actualPlan.start, actualPlan && actualPlan.end),
        'Сотрудник актуального плана': actualPlan && actualPlan.employeeName,
        'ID сотрудника до': inputOrder && inputOrder.employee_id,
        'Сотрудник до': this.getExcelEmployeeName(inputOrder && inputOrder.employee_id),
        'ID сотрудника после': route && route.employee_id,
        'Сотрудник после': this.getRouteExportName(route),
        'ГПХ': route ? (this.isRouteEmployeeGph(route) ? 'Да' : 'Нет') : '',
        'Командирован': route ? (this.isRouteEmployeeOnTrip(route) ? 'Да' : 'Нет') : '',
        'Причина неназначения':
          unassignedOrder && (unassignedOrder.reason || unassignedOrder.unassigned_reason),
      };

      this.flattenExcelObject(inputOrder, 'Исходные данные', record);
      this.flattenExcelObject(resultOrder, 'Результат распределения', record);
      this.flattenExcelObject(unassignedOrder, 'Нераспределенная заявка', record);
      this.flattenExcelObject(analysis, 'AI-анализ', record);
      this.flattenExcelObject(
        route && {
          employee_id: route.employee_id,
          employee_name: this.getRouteExportName(route),
          start_of_shift: route.start_of_shift,
          end_of_shift: route.end_of_shift,
          route_start_time: route.route_start_time,
          route_end_time: route.route_end_time,
          total_orders: route.total_orders,
          total_work_time_minutes: route.total_work_time_minutes,
          total_travel_time_minutes: route.total_travel_time_minutes,
          total_travel_distance_meters: route.total_travel_distance_meters,
        },
        'Маршрут сотрудника',
        record
      );

      return record;
    });
    const headers = records.reduce((columns, record) => {
      Object.keys(record).forEach((key) => {
        if (columns.indexOf(key) === -1) {
          columns.push(key);
        }
      });

      return columns;
    }, []);

    return [headers, ...records.map((record) => headers.map((header) => record[header]))];
  }

  private flattenExcelObject(value, prefix: string, target): void {
    if (value === null || value === undefined) {
      return;
    }

    if (Array.isArray(value)) {
      if (value.every((item) => item === null || item === undefined || typeof item !== 'object')) {
        target[prefix] = value.filter((item) => item !== null && item !== undefined).join('; ');
        return;
      }

      value.forEach((item, index) => this.flattenExcelObject(item, `${prefix} №${index + 1}`, target));
      return;
    }

    if (typeof value === 'object') {
      Object.keys(value).forEach((key) =>
        this.flattenExcelObject(value[key], `${prefix}: ${this.getExcelFieldTitle(key)}`, target)
      );
      return;
    }

    target[prefix] = value;
  }

  private getExcelFieldTitle(key: string): string {
    const titles = {
      id: 'ID',
      name: 'Наименование',
      sequence: 'Порядок в маршруте',
      order_id: 'ID заявки',
      order_name: 'Наименование заявки',
      address_id: 'ID адреса',
      employee_id: 'ID сотрудника',
      employee_name: 'Сотрудник',
      fixed_employee_id: 'ID фиксированного сотрудника',
      date_and_time_of_service_start: 'Начало планового окна',
      date_and_time_of_service_end: 'Конец планового окна',
      time_to_work: 'Расчетное время работы, мин',
      requires_car: 'Требуется автомобиль',
      priority: 'Приоритет',
      latitude: 'Широта',
      longitude: 'Долгота',
      arrival_time: 'Время прибытия',
      work_start_time: 'Начало работ',
      work_end_time: 'Завершение работ',
      service_window_start: 'Начало окна заявки',
      service_window_end: 'Конец окна заявки',
      facility_start_time: 'Начало работы объекта',
      facility_end_time: 'Конец работы объекта',
      travel_time_from_previous_minutes: 'Путь от предыдущей заявки, мин',
      travel_distance_from_previous_meters: 'Расстояние от предыдущей заявки, м',
      reason: 'Причина',
      unassigned_reason: 'Причина неназначения',
      summary: 'Описание',
      reasons: 'Причины',
      recommendations: 'Рекомендации',
      source: 'Источник',
      confidence: 'Уверенность',
      diagnostic_context: 'Диагностический контекст',
      total_orders: 'Всего заявок',
      unassigned_orders: 'Не распределено',
      threshold_ratio: 'Порог',
      manual_analysis_required: 'Требуется ручной анализ',
      start_of_shift: 'Начало смены',
      end_of_shift: 'Конец смены',
      route_start_time: 'Начало маршрута',
      route_end_time: 'Конец маршрута',
      total_work_time_minutes: 'Общее время работ, мин',
      total_travel_time_minutes: 'Общее время в пути, мин',
      total_travel_distance_meters: 'Общая дистанция, м',
    };

    return titles[key] || key.replace(/_/g, ' ');
  }

  private getExcelEmployeeName(employeeId): string {
    const normalizedEmployeeId = this.toNumber(employeeId);
    const employee = this.getInputEmployees().find(
      (candidate) => this.getEmployeeId(candidate) === normalizedEmployeeId
    );

    return this.getEmployeeDisplayName(employee);
  }

  private getExcelEmployeesRows(): any[][] {
    const inputOrders = this.getInputOrders();

    return [
      [
        'ID сотрудника',
        'Сотрудник',
        'ГПХ',
        'Командирован',
        'Начало смены',
        'Конец смены',
        'Транспорт',
        'Заявок было',
        'Заявок стало',
        'Работа, мин',
        'Путь, мин',
        'Дистанция, м',
      ],
      ...this.routes.map((route) => {
        const employeeId = this.toNumber(route && route.employee_id);
        const initialOrdersCount = inputOrders.filter(
          (order) => this.toNumber(order && order.employee_id) === employeeId
        ).length;

        return [
          employeeId,
          this.getRouteExportName(route),
          this.isRouteEmployeeGph(route) ? 'Да' : 'Нет',
          this.isRouteEmployeeOnTrip(route) ? 'Да' : 'Нет',
          route && (route.start_of_shift || route.route_start_time),
          route && (route.end_of_shift || route.route_end_time),
          this.getRouteTransportTitle(route),
          initialOrdersCount,
          this.getRouteOrders(route).length,
          route && route.total_work_time_minutes,
          route && route.total_travel_time_minutes,
          route && route.total_travel_distance_meters,
        ];
      }),
    ];
  }

  private getExcelScheduleRows(): any[][] {
    const rows = [
      [
        'ID сотрудника',
        'Сотрудник',
        'ГПХ',
        'Командирован',
        'Порядок',
        'Номер заявки',
        'Наименование',
        'Адрес',
        'Текущая заявка',
        'Гибкие слоты',
        'Текущие слоты',
        'Актуальный план',
        'Сотрудник актуального плана',
        'Прибытие',
        'Начало работ',
        'Завершение работ',
        'Текущие слоты: начало',
        'Текущие слоты: конец',
        'Расчетное время, мин',
        'Путь от предыдущей, мин',
        'Расстояние от предыдущей, м',
        'Широта',
        'Долгота',
      ],
    ];

    this.routes.forEach((route) => {
      this.getRouteOrders(route).forEach((order) => {
        const inputOrder = this.getInputOrder(order);
        const actualPlan = this.getExcelActualPlan(order);
        const flexibleSlotsStart = order && (order.work_start_time || order.arrival_time);
        const flexibleSlotsEnd = order && order.work_end_time;
        const currentSlotsStart =
          (order && (order.service_window_start || order.facility_start_time)) ||
          (inputOrder && inputOrder.date_and_time_of_service_start);
        const currentSlotsEnd =
          (order && (order.service_window_end || order.facility_end_time)) ||
          (inputOrder && inputOrder.date_and_time_of_service_end);

        rows.push([
          route && route.employee_id,
          this.getRouteExportName(route),
          this.isRouteEmployeeGph(route) ? 'Да' : 'Нет',
          this.isRouteEmployeeOnTrip(route) ? 'Да' : 'Нет',
          order && order.sequence,
          this.getOrderNumber(order),
          order && order.order_name,
          this.getOrderAddress(order),
          this.getExcelCurrentOrderLabel(order),
          this.formatExcelInterval(flexibleSlotsStart, flexibleSlotsEnd),
          this.formatExcelInterval(currentSlotsStart, currentSlotsEnd),
          this.formatExcelInterval(actualPlan && actualPlan.start, actualPlan && actualPlan.end),
          actualPlan && actualPlan.employeeName,
          order && order.arrival_time,
          order && order.work_start_time,
          order && order.work_end_time,
          currentSlotsStart,
          currentSlotsEnd,
          inputOrder && inputOrder.time_to_work,
          order && order.travel_time_from_previous_minutes,
          order && order.travel_distance_from_previous_meters,
          order && order.latitude,
          order && order.longitude,
        ]);
      });
    });

    return rows;
  }

  private getExcelMapRoutesRows(): any[][] {
    return [
      [
        'ID сотрудника',
        'Сотрудник',
        'ГПХ',
        'Командирован',
        'Начальная широта',
        'Начальная долгота',
        'Начальный адрес',
        'Конечная широта',
        'Конечная долгота',
        'Конечный адрес',
        'Точек геометрии',
      ],
      ...this.routes.map((route) => {
        const coordinates = route && route.geometry && route.geometry.coordinates;

        return [
          route && route.employee_id,
          this.getRouteExportName(route),
          this.isRouteEmployeeGph(route) ? 'Да' : 'Нет',
          this.isRouteEmployeeOnTrip(route) ? 'Да' : 'Нет',
          route && route.start_location && route.start_location.latitude,
          route && route.start_location && route.start_location.longitude,
          route && route.start_location && route.start_location.address,
          route && route.end_location && route.end_location.latitude,
          route && route.end_location && route.end_location.longitude,
          route && route.end_location && route.end_location.address,
          Array.isArray(coordinates) ? coordinates.length : 0,
        ];
      }),
    ];
  }

  private getExcelGeometryRows(): any[][] {
    const rows = [['ID сотрудника', 'Сотрудник', '№ точки', 'Долгота', 'Широта']];

    this.routes.forEach((route) => {
      const coordinates = route && route.geometry && route.geometry.coordinates;

      (Array.isArray(coordinates) ? coordinates : []).forEach((coordinate, index) => {
        rows.push([
          route && route.employee_id,
          this.getRouteExportName(route),
          index + 1,
          coordinate && coordinate[0],
          coordinate && coordinate[1],
        ]);
      });
    });

    return rows;
  }

  private getExcelUnassignedRows(): any[][] {
    return [
      [
        'Номер заявки',
        'Наименование',
        'Адрес',
        'Причина',
        'Начало окна',
        'Конец окна',
        'Расчетное время, мин',
        'Сотрудник',
        'Фиксированный сотрудник',
        'Требуется автомобиль',
        'Приоритет',
      ],
      ...this.unassignedOrders.map((order) => {
        const inputOrder = this.getInputOrder(order);

        return [
          this.getOrderNumber(order),
          order && (order.order_name || order.name),
          this.getOrderAddress(order),
          order && (order.reason || order.unassigned_reason),
          inputOrder && inputOrder.date_and_time_of_service_start,
          inputOrder && inputOrder.date_and_time_of_service_end,
          inputOrder && inputOrder.time_to_work,
          inputOrder && inputOrder.employee_id,
          inputOrder && inputOrder.fixed_employee_id,
          inputOrder && inputOrder.requires_car,
          inputOrder && inputOrder.priority,
        ];
      }),
    ];
  }

  private getExcelAnalysisRows(): any[][] {
    return [
      [
        'Номер заявки',
        'Подтип заявки',
        'Слот HD',
        'Описание',
        'Причины',
        'Рекомендации',
        'Источник',
        'Уверенность',
        'Всего заявок',
        'Не распределено',
        'Порог',
        'Требуется ручной анализ',
        'ID сотрудника',
      ],
      ...this.unassignedAnalysis.map((analysis) => {
        const context = analysis && analysis.diagnostic_context;

        return [
          this.getOrderNumber(analysis),
          this.getOrderSubtype(analysis),
          this.getOrderHdSlot(analysis),
          this.formatAnalysisText(analysis && analysis.summary, analysis),
          ((analysis && analysis.reasons) || []).map((reason) => this.formatAnalysisText(reason, analysis)).join('\n'),
          this.getAnalysisRecommendations(analysis).join('\n'),
          this.getAnalysisSource(analysis),
          this.formatConfidence(analysis && analysis.confidence),
          context && context.total_orders,
          context && context.unassigned_orders,
          context && context.threshold_ratio,
          context && context.manual_analysis_required,
          this.getAnalysisEmployeeId(analysis),
        ];
      }),
    ];
  }

  private getExcelInputOrdersRows(): any[][] {
    return [
      [
        'ID',
        'Наименование',
        'Начало окна',
        'Конец окна',
        'Расчетное время, мин',
        'ID сотрудника',
        'ID фиксированного сотрудника',
        'ID адреса',
        'Требуется автомобиль',
        'Приоритет',
      ],
      ...this.getInputOrders().map((order) => [
        order && order.id,
        order && order.name,
        order && order.date_and_time_of_service_start,
        order && order.date_and_time_of_service_end,
        order && order.time_to_work,
        order && order.employee_id,
        order && order.fixed_employee_id,
        order && order.address_id,
        order && order.requires_car,
        order && order.priority,
      ]),
    ];
  }

  private getExcelInputEmployeesRows(): any[][] {
    return [
      [
        'ID',
        'Сотрудник',
        'Начало смены',
        'Конец смены',
        'Начальная широта',
        'Начальная долгота',
        'Начальный адрес',
        'Конечная широта',
        'Конечная долгота',
        'Конечный адрес',
        'Наличие автомобиля',
      ],
      ...this.getInputEmployees().map((employee) => {
        const startLocation = employee && employee.start_location;
        const endLocation = employee && employee.end_location;

        return [
          employee && (employee.id || employee.employee_id),
          this.getEmployeeDisplayName(employee),
          employee && employee.start_of_shift,
          employee && employee.end_of_shift,
          startLocation && startLocation.latitude,
          startLocation && startLocation.longitude,
          startLocation && startLocation.address,
          endLocation && endLocation.latitude,
          endLocation && endLocation.longitude,
          endLocation && endLocation.address,
          this.getCarAvailability(employee),
        ];
      }),
    ];
  }

  private getExcelInputAddressesRows(): any[][] {
    return [
      ['ID', 'Широта', 'Долгота', 'Начало работы объекта', 'Конец работы объекта'],
      ...this.getInputAddresses().map((address) => [
        address && address.id,
        address && address.latitude,
        address && address.longitude,
        address && address.facility_start_time,
        address && address.facility_end_time,
      ]),
    ];
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const objectUrl = URL.createObjectURL(blob);
    const downloadLink = this.document.createElement('a');

    downloadLink.href = objectUrl;
    downloadLink.download = fileName;
    downloadLink.style.display = 'none';
    this.document.body.appendChild(downloadLink);
    downloadLink.click();
    this.document.body.removeChild(downloadLink);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }

  private escapeHtml(value: string): string {
    const container = this.document.createElement('div');

    container.textContent = String(value || '');

    return container.innerHTML;
  }

  private findResultDate(): string | null {
    for (const route of this.routes) {
      if (route.route_start_time) {
        return route.route_start_time;
      }

      const orders = this.getRouteOrders(route);
      const order = orders.find((item) => item && (item.work_start_time || item.arrival_time));

      if (order) {
        return order.work_start_time || order.arrival_time;
      }
    }

    return null;
  }

  private getAnalysisOrderType(analysis): string {
    const ticket = this.getTicket(analysis) || this.getOriginalTicket(analysis);
    const inputOrder = this.getInputOrder(analysis);
    const unassignedOrder = this.getUnassignedOrder(analysis);

    return (
      [
        ticket && ticket.ticket_type,
        ticket && ticket.hd_ticket_type,
        analysis && (analysis.order_type || analysis.ticket_type),
        analysis && (analysis.order_name || analysis.name),
        unassignedOrder && (unassignedOrder.order_type || unassignedOrder.order_name || unassignedOrder.name),
        inputOrder && (inputOrder.order_type || inputOrder.order_name || inputOrder.name),
      ]
        .map((source) => this.getTypeName(source))
        .find((name) => !!name) || ''
    );
  }

  private getDistrictName(source): string {
    if (!source) {
      return '';
    }

    const district = source.district;

    if (district && typeof district === 'object') {
      return String(district.district_name || district.full_name || district.name || '');
    }

    return String(source.district_name || district || '');
  }

  private getDistrictNameById(sources: any[]): string {
    const districtId = sources
      .map((source) => this.getDistrictId(source))
      .find((id) => id !== null && id !== undefined);

    if (!districtId) {
      return '';
    }

    const ticketWithDistrict = this.getTicketsFlatList().find(
      (ticket) => this.getDistrictId(ticket) === districtId
    );

    return this.getDistrictName(ticketWithDistrict);
  }

  private getDistrictId(source): number | null {
    if (!source) {
      return null;
    }

    const district = source.district;
    const districtId =
      source.district_id || (district && typeof district === 'object' ? district.d_id || district.id : null);

    return this.toNumber(districtId);
  }

  private getTicketsFlatList(): any[] {
    const tickets = (this.tickets && this.tickets.dataFlatList) || [];

    return tickets.concat(this.getOriginalTicketsFlatList());
  }

  private getTypeName(source): string {
    if (!source) {
      return '';
    }

    if (typeof source === 'object') {
      return String(source.name || source.title || '');
    }

    return String(source);
  }

  private getAnalysisOrderAddress(analysis): string {
    const address = this.getOrderAddress(analysis);

    if (address && address !== '-') {
      return address;
    }

    const originalTicket = this.getOriginalTicket(analysis);
    const unassignedOrder = this.getUnassignedOrder(analysis);
    const fallbackAddress =
      (originalTicket && originalTicket.hd_address) ||
      (unassignedOrder ? this.getOrderAddress(unassignedOrder) : '');

    return fallbackAddress && fallbackAddress !== '-' ? String(fallbackAddress) : '';
  }

  private getUnassignedOrder(order) {
    const orderId = this.toNumber(order && (order.order_id || order.id));

    if (orderId === null) {
      return null;
    }

    return (
      this.unassignedOrders.find((item) => this.toNumber(item && (item.order_id || item.id)) === orderId) || null
    );
  }

  private getTicket(order) {
    const orderId = this.toNumber(order && order.order_id);
    const tickets = this.tickets && this.tickets.dataFlatList;

    if (!orderId || !tickets) {
      return null;
    }

    return tickets.find((ticket) => this.toNumber(ticket && ticket.id) === orderId) || null;
  }

  private getResultData(result) {
    const resultData = result && result.results && typeof result.results === 'object'
      ? result.results
      : result && result.result && typeof result.result === 'object'
      ? result.result
      : result;
    const input = (resultData && (resultData.input || resultData.inputs)) || (result && (result.input || result.inputs));
    const isApplied =
      resultData && resultData.is_applied !== undefined
        ? resultData.is_applied
        : result && result.is_applied;

    if (resultData && (input || isApplied !== undefined)) {
      return Object.assign({}, resultData, {
        input: input,
        inputs: input,
        is_applied: isApplied,
      });
    }

    return resultData;
  }

  private getInputAddressByOrder(order) {
    const inputOrder = this.getInputOrder(order);
    const addressId = this.toNumber((order && order.address_id) || (inputOrder && inputOrder.address_id));

    if (!addressId) {
      return null;
    }

    return this.getInputAddresses().find((address) => this.toNumber(address && (address.id || address.address_id)) === addressId) || null;
  }

  private getInputOrder(order) {
    const orderId = this.toNumber(order && (order.order_id || order.id));

    if (!orderId) {
      return null;
    }

    return this.getInputOrders().find((inputOrder) => this.toNumber(inputOrder && (inputOrder.id || inputOrder.order_id)) === orderId) || null;
  }

  private getInputOrders(): any[] {
    const input = this.result && (this.result.input || this.result.inputs);

    return (input && input.orders) || [];
  }

  private getInputAddresses(): any[] {
    const input = this.result && (this.result.input || this.result.inputs);

    return (input && input.addresses) || [];
  }

  private getInputEmployees(): any[] {
    const input = this.result && (this.result.input || this.result.inputs);

    return (input && input.employees) || [];
  }

  private getAnalysisEmployeeId(analysis): number | null {
    const context = analysis && analysis.diagnostic_context;
    const inputOrder = this.getInputOrder(analysis);
    const ticket = this.getTicket(analysis);
    const directId =
      (analysis && (analysis.fixed_employee_id || analysis.employee_id)) ||
      (context && (context.fixed_employee_id || context.employee_id)) ||
      (inputOrder && (inputOrder.fixed_employee_id || inputOrder.fixed_employee)) ||
      (ticket && (ticket.fixed_employee_id || ticket.fixed_employee));
    const employeeId = this.toNumber(directId);

    if (employeeId) {
      return employeeId;
    }

    const recommendations = analysis && analysis.recommendations;
    const text = [
      analysis && analysis.summary,
      ...((analysis && analysis.reasons) || []),
      ...(Array.isArray(recommendations) ? recommendations : [recommendations]),
    ]
      .filter((part) => !!part)
      .join(' ');
    const match = text.match(/fixed_employee_id\D*(\d+)|сотрудник\D+ID\s*(\d+)/i);

    return this.toNumber(match && (match[1] || match[2]));
  }

  private getAnalysisEmployeeName(analysis, employeeId: number): string {
    const context = analysis && analysis.diagnostic_context;
    const inputOrder = this.getInputOrder(analysis);
    const ticket = this.getTicket(analysis);
    const directName =
      (analysis && analysis.fixed_employee_name) ||
      (context && context.fixed_employee_name) ||
      (inputOrder && inputOrder.fixed_employee_name) ||
      (ticket && ticket.fixed_employee_name);

    if (directName) {
      return String(directName);
    }

    const settings = this.automaticDistribution && this.automaticDistribution.lastSettings;
    const candidates = this.getInputEmployees()
      .concat((settings && settings.employees) || [])
      .concat(this.routes)
      .concat([
        analysis && analysis.fixed_employee,
        context && context.fixed_employee,
        inputOrder && inputOrder.fixed_employee,
        ticket && ticket.fixed_employee,
      ])
      .filter((candidate) => !!candidate && typeof candidate === 'object');
    const employee = candidates.find((candidate) => this.getEmployeeId(candidate) === employeeId);

    return this.getEmployeeDisplayName(employee);
  }

  private getEmployeeId(employee): number | null {
    return this.toNumber(employee && (employee.user_id || employee.employee_id || employee.id || employee.e_id));
  }

  private getEmployeeDisplayName(employee): string {
    if (!employee) {
      return '';
    }

    const explicitName = employee.employee_name || employee.full_name || employee.fio || employee.name;

    if (explicitName) {
      return String(explicitName);
    }

    return [
      employee.last_name || employee.surname,
      employee.first_name,
      employee.patronymic_name || employee.middle_name,
    ]
      .filter((name) => !!name)
      .join(' ');
  }

  private getRouteEmployeeSources(route): any[] {
    const employeeId = this.toNumber(route && route.employee_id);

    return this.getInputEmployees()
      .concat([route])
      .concat(this.distributionEmployees)
      .filter((employee) => employee && (employee === route || this.getEmployeeId(employee) === employeeId));
  }

  private isTruthyMarker(value): boolean {
    return value === true || value === 1 || value === '1';
  }

  private getRouteTransportType(route): string {
    const routeTransportType = this.normalizeTransportType(route && route.transport_type);

    if (routeTransportType) {
      return routeTransportType;
    }

    const employeeId = this.toNumber(route && route.employee_id);
    const employee = this.getInputEmployees().find(
      (item) => this.toNumber(item && (item.id || item.employee_id)) === employeeId
    );
    const employeeTransportType = this.normalizeTransportType(employee && employee.transport_type);

    if (employeeTransportType) {
      return employeeTransportType;
    }

    return this.hasEmployeeCar(route) ? 'driving' : 'walking';
  }

  private normalizeTransportType(value): string | null {
    const transportType = value === null || value === undefined ? '' : String(value).toLowerCase();

    return ['walking', 'driving', 'public_transport'].indexOf(transportType) > -1 ? transportType : null;
  }

  private hasEmployeeCar(route): boolean {
    const routeAvailability = this.getCarAvailability(route);

    if (routeAvailability !== null) {
      return routeAvailability;
    }

    const employeeId = this.toNumber(route && route.employee_id);
    const employee = this.getInputEmployees().find(
      (item) => this.toNumber(item && (item.id || item.employee_id)) === employeeId
    );

    return this.getCarAvailability(employee) === true;
  }

  private getCarAvailability(entity): boolean | null {
    if (!entity) {
      return null;
    }

    if (entity.availability_of_a_car !== undefined && entity.availability_of_a_car !== null) {
      return entity.availability_of_a_car === true;
    }

    const restriction = (entity.restrictions || []).find(
      (item) => item && item.availability_of_a_car !== undefined && item.availability_of_a_car !== null
    );

    return restriction ? restriction.availability_of_a_car === true : null;
  }

  private getAddressTitle(address): string {
    if (!address) {
      return '';
    }

    return address.address || address.full_address || address.name || '';
  }

  private getCoordinateText(source): string {
    const latitude = this.toCoordinate(source && (source.latitude || source.lat));
    const longitude = this.toCoordinate(source && (source.longitude || source.lon || source.lng));

    if (latitude === null || longitude === null) {
      return '';
    }

    return `${latitude}, ${longitude}`;
  }

  private getSummaryNumber(key: string, fallback: number): number {
    if (!this.summary || this.summary[key] === null || this.summary[key] === undefined || this.summary[key] === '') {
      return fallback;
    }

    const value = Number(this.summary[key]);

    return Number.isFinite(value) ? value : fallback;
  }

  private toNumber(value): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numberValue = Number(value);

    return Number.isFinite(numberValue) ? numberValue : null;
  }

  private toCoordinate(value): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const coordinate = Number(String(value).replace(',', '.'));

    return Number.isFinite(coordinate) ? coordinate : null;
  }
}
